import React, { createContext, useCallback, useContext, useState } from "react";

import { ConfirmDialog, Content } from "../../components/ConfirmDialogNew/ConfirmDialog";

type Confirm = (confirmOptions?: Content) => Promise<void>;

type ConfirmContextValue = { confirm: Confirm; handleClose: () => void };

const ConfirmContext = createContext<ConfirmContextValue>({
  confirm: async () => {},
  handleClose: () => {},
});

export type ConfirmProviderProps = React.PropsWithChildren<{ defaultOptions?: Content }>;

export const ConfirmProvider: React.FC<ConfirmProviderProps> = ({ children, defaultOptions }) => {
  const [options, setOptions] = useState({ ...defaultOptions });

  const [resolveReject, setResolveReject] = useState<((value: void) => void)[]>([]);

  const [resolve, reject] = resolveReject;

  const handleClose = useCallback(() => {
    setResolveReject([]);
  }, []);

  const handleCancel = useCallback(() => {
    reject();
    handleClose();
  }, [handleClose, reject]);

  const handleConfirm = useCallback(() => {
    resolve();
    handleClose();
  }, [handleClose, resolve]);

  const confirm = useCallback<Confirm>(
    (confirmOptions?: Content) =>
      new Promise((resolve, reject) => {
        setOptions({
          ...defaultOptions,
          ...confirmOptions,
        });

        setResolveReject([resolve, reject]);
      }),
    [defaultOptions]
  );

  return (
    <ConfirmContext.Provider value={{ confirm, handleClose }}>
      {children}

      <ConfirmDialog
        isOpen={resolveReject.length === 2}
        content={options}
        onClose={handleClose}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmContextValue => useContext(ConfirmContext);
